import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../client/config';
import { Button } from './ui';
import { ContentSkeleton } from './skeleton/ContentSkeleton';
import { ModalShell } from './menu';
import { BoPage, Section, formatBytes, formatDateTime } from '../pages/bo/ui';

// =============================================================================
// 저장 공간 (2026-10-12 대교) — 운영사 > 설정의 패널, 그리고 구성 관리 '저장 공간' 카드의 자세히 보기(고객).
// 서버 데이터 디스크 전체·사용·여유와 무엇이 얼마나 쓰는지. 하드링크로 묶인 파일은 한 번만 센다 —
// '서버 내 백업' 칸은 원본과 묶이지 않아 백업 때문에 따로 쓰는 양이다. 80% 경고, 90% 위험.
// =============================================================================

type Volume = { totalBytes: number; usedBytes: number; freeBytes: number; usedRatio: number };
type Category = 'media' | 'versions' | 'backups' | 'database' | 'thumbnails' | 'screenshots' | 'otherData';
export type StorageUsage = {
  measuredAt: string; volume: Volume; system: Volume | null;
  categories: Record<Category, { bytes: number; files: number }>;
  unclassifiedBytes: number;
  backups: { localBytes: number; extraBytes: number; objectBytes: number };
};

const WARN = 0.8;
const CRITICAL = 0.9;
/** 운영사 화면 안내. 서버 디스크는 운영사만 본다(고객은 저장 한도만 — StorageDetailModal). */
const COPY = {
  idle: '80%를 넘으면 운영자에게 경고 메일, 90%를 넘으면 위험 메일을 보내고 새 업로드를 막습니다.',
  warning: '80%를 넘었습니다. 쓰지 않는 미디어 정리나 디스크 증설을 준비하세요.',
  critical: '90%를 넘어 새 업로드를 막고 있습니다. 가득 차면 저장·백업도 실패합니다. 쓰지 않는 미디어를 정리하거나 디스크를 늘리세요.',
};

/** 막대·범례 순서. 색은 같은 계열의 진하기로 나누고, 백업만 다른 색으로 눈에 띄게 한다. */
// 이름은 2026-10-13 대교 확정: 미디어 승인본 · 서버 내 백업 · 데이터베이스 · 미리보기 이미지, 기기 스크린샷은 기타 데이터에 합친다.
const ROWS: Array<{ key: Category | 'unclassified'; label: string; hint: string; color: string; also?: Category[] }> = [
  { key: 'media', label: '미디어 원본', hint: '업로드한 이미지·영상과 변환본', color: 'bg-accent' },
  { key: 'versions', label: '미디어 승인본', hint: '승인 당시 그대로 보관한 이미지·영상 — 원본을 바꾸거나 지워도 남습니다', color: 'bg-accent/55' },
  { key: 'backups', label: '서버 내 백업', hint: '원본과 묶이지 않아 따로 쓰는 양 — 데이터베이스 사본·미리보기 이미지·지운 지 7일 안 된 미디어', color: 'bg-warning' },
  { key: 'database', label: '데이터베이스', hint: '', color: 'bg-green' },
  { key: 'thumbnails', label: '미리보기 이미지', hint: '', color: 'bg-green/55' },
  { key: 'otherData', label: '기타 데이터', hint: '기기 스크린샷 등', color: 'bg-text-dim/60', also: ['screenshots'] },
  { key: 'unclassified', label: '앱·로그·시스템', hint: '앱 코드·로그·파일시스템 몫', color: 'bg-text-dim/35' },
];
/** 한 줄의 값 — '기타 데이터'는 기기 스크린샷을 함께 담는다. */
const rowTotal = (row: (typeof ROWS)[number], categories: Record<Category, { bytes: number; files: number }>, field: 'bytes' | 'files') =>
  row.key === 'unclassified' ? 0 : [row.key, ...(row.also ?? [])].reduce((sum, key) => sum + categories[key][field], 0);

const percent = (ratio: number) => `${Math.round(ratio * 100)}%`;
const levelOf = (ratio: number) => ratio >= CRITICAL ? 'critical' : ratio >= WARN ? 'warning' : null;

function UsageBar({ volume, segments }: { volume: Volume; segments: Array<{ key: string; label: string; bytes: number; color: string }> }): JSX.Element {
  const usable = volume.usedBytes + volume.freeBytes || 1;
  return <div className="relative">
    <div className="flex h-5 w-full overflow-hidden rounded bg-panel-3" role="img"
      aria-label={`사용 ${formatBytes(volume.usedBytes)}, 여유 ${formatBytes(volume.freeBytes)}, ${percent(volume.usedRatio)} 사용`}>
      {segments.filter((segment) => segment.bytes > 0).map((segment) =>
        <div key={segment.key} className={`${segment.color} h-full`} style={{ width: `${(segment.bytes / usable) * 100}%` }} title={`${segment.label} ${formatBytes(segment.bytes)}`} />)}
    </div>
    {[WARN, CRITICAL].map((mark) => <div key={mark} aria-hidden="true" className="absolute top-[-4px] h-[28px] border-l border-dashed border-text-dim"
      style={{ left: `${mark * 100}%` }}><span className="absolute top-[30px] -translate-x-1/2 text-[11px] text-text-dim">{percent(mark)}</span></div>)}
  </div>;
}

/** 디스크 막대·항목 표·백업 용량. 운영사 패널과 고객 모달이 같이 쓴다. */
export function StorageUsageDetails({ data }: { data: StorageUsage }): JSX.Element {
  const level = levelOf(data.volume.usedRatio);
  const copy = COPY;
  const segments = ROWS.map((row) => ({ ...row, bytes: row.key === 'unclassified' ? data.unclassifiedBytes : rowTotal(row, data.categories, 'bytes') }));
  const sharedBackup = Math.max(0, data.backups.localBytes - data.backups.extraBytes);
  return <div>
    <p className="mb-2 text-[13px] text-text-dim">데이터 디스크 — 데이터베이스·미디어·서버 내 백업이 들어 있는 디스크</p>
    <div className="mb-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
      <p className="text-[28px] font-semibold leading-none text-text">{percent(data.volume.usedRatio)}</p>
      <p className="text-[15px] text-text">{formatBytes(data.volume.usedBytes)} 사용 · 전체 {formatBytes(data.volume.totalBytes)}</p>
      <p className="text-[15px] text-text-dim">여유 {formatBytes(data.volume.freeBytes)}</p>
    </div>
    <UsageBar volume={data.volume} segments={segments} />
    <div className="mt-8 min-h-[22px] text-[14px]" role="status">
      <p className={level === 'critical' ? 'text-red' : level === 'warning' ? 'text-warning' : 'text-text-dim'}>{copy[level ?? 'idle']}</p>
    </div>
    <table className="mt-3 w-full text-left text-[14px] text-text">
      <caption className="sr-only">데이터 디스크 사용 내역</caption>
      <thead className="border-b border-line text-[13px] text-text-dim"><tr>
        <th scope="col" className="py-2 font-medium">항목</th>
        <th scope="col" className="py-2 text-right font-medium">용량</th>
        <th scope="col" className="hidden py-2 pl-4 text-right font-medium sm:table-cell">파일</th>
      </tr></thead>
      <tbody>
        {segments.map((row) => <tr key={row.key} className="border-b border-line last:border-0">
          <td className="py-2"><div className="flex items-start gap-2">
            <span aria-hidden="true" className={`${row.color} mt-[5px] h-3 w-3 shrink-0 rounded-sm`} />
            <div className="min-w-0"><p>{row.label}</p>{row.hint && <p className="text-[12px] text-text-dim">{row.hint}</p>}</div>
          </div></td>
          <td className="py-2 text-right tabular-nums">{formatBytes(row.bytes)}</td>
          <td className="hidden py-2 pl-4 text-right tabular-nums text-text-dim sm:table-cell">{row.key === 'unclassified' ? '—' : `${rowTotal(row, data.categories, 'files').toLocaleString('ko-KR')}개`}</td>
        </tr>)}
        <tr>
          <td className="py-2"><div className="flex items-center gap-2"><span aria-hidden="true" className="h-3 w-3 shrink-0 rounded-sm border border-line-strong bg-panel-3" />여유</div></td>
          <td className="py-2 text-right tabular-nums">{formatBytes(data.volume.freeBytes)}</td>
          <td className="hidden sm:table-cell" />
        </tr>
      </tbody>
    </table>

    <h3 className="mb-3 mt-6 text-[15px] font-semibold text-text">백업 용량</h3>
    <dl className="grid gap-4 text-[14px] sm:grid-cols-3">
      <div><dt className="text-text-dim">보관 중인 백업</dt><dd className="mt-1 text-[20px] font-semibold tabular-nums text-text">{formatBytes(data.backups.objectBytes)}</dd>
        <dd className="mt-1 text-[12px] text-text-dim">지금 되살릴 수 있는 파일 총량. S3를 쓰면 같은 세대가 S3에도 올라갑니다.</dd></div>
      <div><dt className="text-text-dim">서버 내 백업 폴더</dt><dd className="mt-1 text-[20px] font-semibold tabular-nums text-text">{formatBytes(data.backups.localBytes)}</dd>
        <dd className="mt-1 text-[12px] text-text-dim">그중 {formatBytes(sharedBackup)}는 원본과 같은 파일(하드링크)이라 디스크를 더 쓰지 않습니다.</dd></div>
      <div><dt className="text-text-dim">백업이 디스크를 더 쓰는 양</dt><dd className="mt-1 text-[20px] font-semibold tabular-nums text-text">{formatBytes(data.backups.extraBytes)}</dd>
        <dd className="mt-1 text-[12px] text-text-dim">위 표의 '서버 내 백업'과 같은 값입니다.</dd></div>
    </dl>

    <p className="mt-5 text-[12px] text-text-dim">
      {formatDateTime(data.measuredAt)}에 잼 · {data.system
        ? `시스템 디스크(운영체제) ${formatBytes(data.system.usedBytes)} / ${formatBytes(data.system.totalBytes)} 사용(${percent(data.system.usedRatio)})`
        : '시스템 디스크(운영체제)는 데이터와 같은 디스크'}
    </p>
  </div>;
}

/** 한 번 불러오고, 새로고침으로 다시 읽는다. */
function useStorage<T>(url: string, forbidden: string): { data: T | null; fetching: boolean; error: string | null; refresh: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const refresh = useCallback(async (): Promise<void> => {
    controller.current?.abort();
    const current = new AbortController(); controller.current = current;
    setFetching(true);
    try {
      const response = await fetch(url, { credentials: 'include', signal: current.signal });
      if (response.status === 401 || response.status === 403) throw new Error(forbidden);
      if (!response.ok) throw new Error(`저장 공간을 재지 못했습니다 (HTTP ${response.status}).`);
      const next = await response.json() as T;
      if (!current.signal.aborted) { setData(next); setError(null); }
    } catch (reason) {
      if (!current.signal.aborted) setError(reason instanceof Error ? reason.message : String(reason));
    } finally { if (!current.signal.aborted) setFetching(false); }
  }, [url, forbidden]);
  useEffect(() => { void refresh(); return () => controller.current?.abort(); }, [refresh]);
  return { data, fetching, error, refresh: () => void refresh() };
}

/** 운영사 > 설정 > 저장 공간. */
export function StorageUsagePanel(): JSX.Element {
  const { data, fetching, error, refresh } = useStorage<StorageUsage>(`${API_BASE}/api/bo/backups/storage`, 'BO 운영자 권한을 확인해 주세요.');
  const remeasure = <Button disabled={fetching} onClick={refresh}>새로고침</Button>;
  return <BoPage title="저장 공간">
    {error && <p role="alert" className="rounded border border-red/40 bg-red/10 p-3 text-[14px] text-red">{error}</p>}
    {!data && fetching && !error && <ContentSkeleton label="저장 공간을 재는 중…" variant="split" />}
    {(data || error) && <Section title="저장 공간" actions={remeasure}>
      {data ? <StorageUsageDetails data={data} /> : <p className="text-[14px] text-text-dim">새로고침을 눌러 주세요.</p>}
    </Section>}
  </BoPage>;
}

/**
 * 저장 한도(고객) — 한도(플랜)를 콘텐츠가 쓰는 디스크 합계에 건다: 원본·변환본, 미디어 승인본, 서버 내 백업, 데이터베이스·미리보기 이미지 등.
 * 업로드 원본만 보이면 백업·승인본이 얼마를 쓰는지 몰라 '한도가 틀렸다'로 읽힌다(2026-10-12 대교).
 * 고객사마다 자기 몫만 본다 — 공용(백업·DB)은 사용 비율로 나눈 값. 서버 디스크 전체·여유는 운영사만 본다.
 */
type OrgStorage = { quotaBytes: number | null; usedBytes: number; categories: StorageUsage['categories'] | null; measuredAt: string | null };

function StorageLimitBlock({ data }: { data: OrgStorage }): JSX.Element {
  const parts = data.categories ? ROWS.filter((row) => row.key !== 'unclassified').map((row) => ({ ...row, bytes: rowTotal(row, data.categories!, 'bytes') })) : [];
  const ratio = data.quotaBytes ? data.usedBytes / data.quotaBytes : null;
  const level = ratio === null ? null : levelOf(ratio);
  const scale = data.quotaBytes ?? Math.max(data.usedBytes, 1);
  return <section aria-labelledby="storage-limit-title">
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h3 id="storage-limit-title" className="text-[15px] font-semibold text-text">저장 한도</h3>
      <p className="text-[15px] tabular-nums text-text">{data.quotaBytes
        ? <>{formatBytes(data.usedBytes)} / {formatBytes(data.quotaBytes)} <span className={level === 'critical' ? 'text-red' : level === 'warning' ? 'text-warning' : 'text-text-dim'}>· {percent(ratio ?? 0)} 사용</span></>
        : <>{formatBytes(data.usedBytes)} <span className="text-text-dim">· 한도 없음</span></>}</p>
    </div>
    <div className="mt-3 flex h-3 w-full overflow-hidden rounded bg-panel-3" role="img"
      aria-label={`저장 한도 ${data.quotaBytes ? formatBytes(data.quotaBytes) : '없음'} 중 ${formatBytes(data.usedBytes)} 사용${parts.length ? ` — ${parts.filter((part) => part.bytes > 0).map((part) => `${part.label} ${formatBytes(part.bytes)}`).join(', ')}` : ''}`}>
      {parts.length ? parts.filter((part) => part.bytes > 0).map((part) =>
        <div key={part.key} className={`${part.color} h-full`} style={{ width: `${Math.min(100, (part.bytes / scale) * 100)}%` }} />)
        : <div className="h-full bg-accent" style={{ width: `${Math.min(100, (data.usedBytes / scale) * 100)}%` }} />}
    </div>
    {level && <p className="mt-3 text-[14px]" role="status"><span className={level === 'critical' ? 'text-red' : 'text-warning'}>
      {level === 'critical' ? '한도의 90%를 넘었습니다. 쓰지 않는 미디어를 정리해 주세요. 한도에 닿으면 새 업로드가 막힙니다.' : '한도의 80%를 넘었습니다. 쓰지 않는 미디어를 정리하거나 운영사(H9)와 한도 조정을 협의하세요.'}</span></p>}
    {parts.length > 0 && <table className="mt-4 w-full text-left text-[14px] text-text">
      <caption className="sr-only">저장 한도 사용 내역</caption>
      <thead className="border-b border-line text-[13px] text-text-dim"><tr>
        <th scope="col" className="py-2 font-medium">항목</th>
        <th scope="col" className="py-2 text-right font-medium">용량</th>
      </tr></thead>
      <tbody>{parts.map((part) => <tr key={part.key} className="border-b border-line last:border-0">
        <td className="py-2"><div className="flex items-start gap-2">
          <span aria-hidden="true" className={`${part.color} mt-[5px] h-3 w-3 shrink-0 rounded-sm`} />
          <div className="min-w-0"><p>{part.label}</p>{part.hint && <p className="text-[12px] text-text-dim">{part.hint}</p>}</div>
        </div></td>
        <td className="py-2 text-right tabular-nums">{formatBytes(part.bytes)}</td>
      </tr>)}</tbody>
    </table>}
    {/* 납품 서버는 고객사 한 곳이 쓴다 — '우리 조직'·'여러 조직' 표현은 빼고 계산 방식만 적는다(2026-10-13 대교 문구). */}
    <p className="mt-4 text-[12px] text-text-dim">미디어 원본·미디어 승인본·미리보기 이미지와 서버 내 백업·데이터베이스 중 사용 비율만큼을 더한 값입니다.<br />
      한도에 닿으면 새 업로드가 막힙니다.{data.measuredAt ? ` ${formatDateTime(data.measuredAt)}.` : ''}</p>
  </section>;
}

/** 구성 관리 '저장 공간' 카드의 자세히 보기(고객) — 저장 한도와 항목별 사용량. */
export function StorageDetailModal({ onClose }: { onClose: () => void }): JSX.Element {
  const { data, fetching, error, refresh } = useStorage<OrgStorage>(`${API_BASE}/api/orgs/current/storage`, '저장 공간을 볼 권한이 없습니다.');
  return <ModalShell title="저장 공간" onClose={onClose} size="md" panelClassName="w-[640px] max-w-[calc(100vw-32px)] h-[min(640px,calc(100vh-48px))]"
    footer={<div className="flex justify-end gap-2">
      <Button disabled={fetching} onClick={refresh}>새로고침</Button>
      <Button variant="primary" onClick={onClose}>닫기</Button>
    </div>}>
    {error && <p role="alert" className="mb-4 rounded border border-red/40 bg-red/10 p-3 text-[14px] text-red">{error}</p>}
    {!data && fetching && !error && <ContentSkeleton label="저장 공간을 재는 중…" variant="split" />}
    {data && <StorageLimitBlock data={data} />}
  </ModalShell>;
}
